import { MenuItem, Select } from "@mui/material";
import NumberInput from "components/atoms/NumberInput/NumberInput";
import { Header } from "components/atoms/Typography";
import MUITheme from "components/MUITheme";
import LoadreductionEffectInput from "./LoadReductionEffectInput";
import RevenueEffectInput from "./RevenueEffectInput";
import ConsumptionReductionEffectInput from "./ConsumptionReductionEffectInput";
import FuelCostReductionEffectInput from "./FuelCostReductionEffectInput";
import CarbonIntensityReductionEffectInput from "./CarbonIntensityReductionEffect";

export const DEFAULT_MEASURE_INPUTS = {
    year: new Date().getFullYear() + 1,
    crmId: "",
    category: null,
    sizeUnit: null,
    description: null,
    lifetime: null,
    measureCost: null,
    measureSize: null,
    name: null,
    totalIncentives: null,
    likeForLikeCost: null,
    landlordShareCost: null,
    landlordShareElectricity: null,
    landlordShareNaturalGas: null,
    landlordShareSteam: null,
    userProvidedLandlordShareCost: false,
    userProvidedLandlordShareElectricity: false,
    userProvidedLandlordShareNaturalGas: false,
    userProvidedLandlordShareSteam: false,
    userProvidedCost: false,
    userProvidedIncentives: false,
    userProvidedLikeForLikeCost: false,
    userProvidedSize: false,
    loadReductionEffects: [],
    revenueEffect: null,
    consumptionReductionEffects: [],
    fuelCostReductionEffects: [],
    carbonIntensityReductionEffects: [],
    notes: null,
    implementationStatus: null,
};

const MEASURE_CATEGORIES = [
    { value: "Heating/Cooling", label: "Heating and cooling" },
    { value: "Heating", label: "Heating" },
    { value: "Cooling", label: "Cooling" },
    { value: "Conveying Equipment", label: "Conveying Equipment" },
    { value: "Electrical", label: "Electrical" },
    { value: "Envelope", label: "Envelope" },
    { value: "Process", label: "Process" },
    { value: "Controls", label: "Controls" },
    { value: "Domestic Hot Water", label: "Domestic Hot Water" },
    { value: "Ventilation", label: "Ventilation" },
    { value: "Lighting", label: "Lighting" },
    { value: "Renewables", label: "Renewables" },
    { value: "Other", label: "Other" },
];

const SIZE_UNITS = [
    { value: "kw", label: "Kilowatt" },
    { value: "m2", label: "Square Metre" },
    { value: "m2_of_roof_area", label: "Square Metre of Roof Area" },
    { value: "m2_of_gfa", label: "Square Metre of GFA" },
    { value: "m2_of_window_area", label: "Square Metre of Window Area" },
    { value: "seal", label: "Seal" },
    { value: "ton", label: "Ton" },
    { value: "dryer", label: "Dryer" },
    { value: "zone", label: "Zone" },
    { value: "suite", label: "Suite" },
    { value: "cfm", label: "CFM" },
    {
        value: "cfm_of_ahu_supply_air_rate",
        label: "CFM of AHU Supply Air Rate",
    },
    { value: "elevator", label: "Elevator" },
    { value: "escalator", label: "Escalator" },
    { value: "heating_load_cop", label: "Heating Load COP" },
    { value: "percent", label: "Percent" },
    { value: "items", label: "Items" },
    { value: "none", label: "Other" },
];

const IMPLEMENTATION_STATUSES = [
    { value: "proposed", label: "Proposed" },
    { value: "developing", label: "Developing" },
    { value: "implementing", label: "Implementing" },
    { value: "complete", label: "Complete" },
    { value: "cancelled", label: "Cancelled" },
    { value: "budgeted", label: "Budgeted" },
    { value: "budget rejected", label: "Budget rejected" },
];

function GenericMeasureInputs({
    inputs,
    setInputs,
    measure,
    exclude = [],
}: {
    inputs: CarbonReductionMeasureInput;
    setInputs: (inputs: CarbonReductionMeasureInput) => void;
    measure?: CustomPlanDataSource;
    exclude?: string[];
}) {
    return (
        <div>
            <div className="filter-container">
                <Header size="small">Measure name</Header>
                <input
                    key="measureName"
                    required={true}
                    type="text"
                    value={inputs.name || ""}
                    className="filter-input"
                    onChange={(e) => {
                        const name = e.target.value;
                        setInputs({ ...inputs, name });
                    }}
                    placeholder="Enter project name"
                    maxLength={250}
                />
            </div>
            <div className="filter-container">
                <Header size="small">Measure description</Header>
                <textarea
                    key="measureDescription"
                    required={false}
                    style={{ height: "100px", resize: "none" }}
                    value={inputs.description || ""}
                    className="filter-input"
                    onChange={(e) => {
                        const description = e.target.value;
                        setInputs({ ...inputs, description });
                    }}
                    placeholder={measure?.description || "Enter a measure description"}
                />
            </div>
            <div className="filter-container">
                <Header size="small">Measure category</Header>
                <MUITheme primaryColor="#EB03AD">
                    <Select
                        value={inputs.category || ""}
                        onChange={(e) => setInputs({ ...inputs, category: e.target.value })}
                        style={{
                            width: "100%",
                            height: "35px",
                            minWidth: 0,
                        }}
                        required
                    >
                        {MEASURE_CATEGORIES.map((category) => (
                            <MenuItem key={category.value} value={category.value}>
                                {category.label}
                            </MenuItem>
                        ))}
                    </Select>
                </MUITheme>
            </div>
            <div className="filter-container">
                <Header size="small">Measure life</Header>
                <NumberInput
                    key="measureLife"
                    required={false}
                    value={inputs.lifetime || ""}
                    className="filter-input"
                    onChange={(e) => {
                        if (e.target.value === "") {
                            setInputs({ ...inputs, lifetime: null });
                        }
                        const lifetime = Number(e.target.value);
                        if (lifetime < 1) return;
                        setInputs({ ...inputs, lifetime });
                    }}
                    placeholder="Enter lifespan of measure (greater than or equal to 1)"
                />
            </div>
            {!exclude.includes("measureCost") && (
                <div className="filter-container">
                    <Header size="small">Measure cost</Header>
                    <NumberInput
                        key="measureCost"
                        required={true}
                        value={inputs.measureCost || ""}
                        className="filter-input"
                        onChange={(e) => {
                            const measureCost = Number(e.target.value);
                            setInputs({ ...inputs, measureCost });
                        }}
                        placeholder="Enter cost to install measure"
                    />
                </div>
            )}
            {!exclude.includes("measureSize") && (
                <div className="filter-container">
                    <Header size="small">Measure size</Header>
                    <NumberInput
                        key="measureSize"
                        required={true}
                        value={inputs.measureSize || ""}
                        className="filter-input"
                        onChange={(e) => {
                            const measureSize = Number(e.target.value);
                            setInputs({ ...inputs, measureSize });
                        }}
                        placeholder="Enter the size of your measure"
                    />
                </div>
            )}
            <div className="filter-container">
                <Header size="small">Size unit</Header>
                <MUITheme primaryColor="#EB03AD">
                    <Select
                        value={inputs.sizeUnit || ""}
                        onChange={(e) => setInputs({ ...inputs, sizeUnit: e.target.value })}
                        style={{
                            width: "100%",
                            height: "35px",
                            minWidth: 0,
                        }}
                        required
                    >
                        {SIZE_UNITS.map((unit) => (
                            <MenuItem key={unit.value} value={unit.value}>
                                {unit.label}
                            </MenuItem>
                        ))}
                    </Select>
                </MUITheme>
            </div>
            <div className="filter-container">
                <Header size="small">Implementation status</Header>
                <MUITheme primaryColor="#EB03AD">
                    <Select
                        value={inputs.implementationStatus || ""}
                        onChange={(e) =>
                            setInputs({
                                ...inputs,
                                implementationStatus: e.target.value,
                            })
                        }
                        style={{
                            width: "100%",
                            height: "35px",
                            minWidth: 0,
                        }}
                    >
                        {IMPLEMENTATION_STATUSES.map((status) => (
                            <MenuItem key={status.value} value={status.value}>
                                {status.label}
                            </MenuItem>
                        ))}
                    </Select>
                </MUITheme>
            </div>
            <div className="filter-container">
                <Header size="small">Notes</Header>
                <textarea
                    key="measureNotes"
                    required={false}
                    style={{ height: "100px", resize: "none" }}
                    value={inputs.notes || ""}
                    className="filter-input"
                    onChange={(e) => {
                        const notes = e.target.value;
                        setInputs({ ...inputs, notes });
                    }}
                    placeholder="Enter any additional notes"
                />
            </div>
            {!exclude.includes("loadReductionEffects") && (
                <LoadreductionEffectInput
                    effects={inputs.loadReductionEffects}
                    setEffects={(loadReductionEffects) =>
                        setInputs({
                            ...inputs,
                            loadReductionEffects,
                        })
                    }
                />
            )}
            {!exclude.includes("revenueEffect") && (
                <RevenueEffectInput
                    inputs={inputs.revenueEffect}
                    setInputs={(revenueInputs) =>
                        setInputs({
                            ...inputs,
                            revenueEffect: revenueInputs,
                        })
                    }
                />
            )}
            {!exclude.includes("consumptionReductionEffects") && (
                <ConsumptionReductionEffectInput
                    consumptionReductionInputs={inputs.consumptionReductionEffects}
                    setConsumptionReductionInputs={(consumptionReductionInputs) => {
                        setInputs({
                            ...inputs,
                            consumptionReductionEffects: consumptionReductionInputs,
                        });
                    }}
                />
            )}
            {!exclude.includes("fuelCostReductionEffects") && (
                <FuelCostReductionEffectInput
                    inputs={inputs.fuelCostReductionEffects}
                    setInputs={(newFuelCostReductionEffects) => {
                        setInputs({
                            ...inputs,
                            fuelCostReductionEffects: newFuelCostReductionEffects,
                        });
                    }}
                />
            )}
            {!exclude.includes("carbonIntensityReductionEffects") && (
                <CarbonIntensityReductionEffectInput
                    carbonIntensityReductionInputs={inputs.carbonIntensityReductionEffects}
                    setCarbonIntensityReductionInputs={(carbonIntensityReductionInputs) => {
                        setInputs({
                            ...inputs,
                            carbonIntensityReductionEffects: carbonIntensityReductionInputs,
                        });
                    }}
                />
            )}
        </div>
    );
}

export default GenericMeasureInputs;
